import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button, Icon, Message, Modal } from "semantic-ui-react";

import api from "api";

import { FileChooser } from "components/lib/Choosers";

const DOCX_ACCEPT_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
].join(",");

type ImportWordModalProps = {
  open: boolean;
  onClose: () => void;
  onDownloadWord: () => void;
  ideaId: string;
  onImported: (response: any) => void;
};

const ImportWordModal = ({
  open,
  onClose,
  onDownloadWord,
  ideaId,
  onImported,
}: ImportWordModalProps) => {
  const { t } = useTranslation();
  const [uploadedFile, setUploadedFile] = useState<{
    savedName: string;
    fileName: string;
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const resetStateAndClose = useCallback(() => {
    setUploadedFile(null);
    setIsImporting(false);
    onClose?.();
  }, [onClose]);

  const importFromWord = useCallback(() => {
    if (!uploadedFile?.savedName) {
      return;
    }

    setIsImporting(true);
    api.ideas.importFromWord(
      ideaId,
      { fileName: uploadedFile.savedName },
      (response) => {
        setIsImporting(false);
        if (!response?.success) {
          toast.error(response?.message || "No new content was imported");
          return;
        }

        toast.success(
          t("ideas.importWord.success", {
            count: response.importedFieldCount || 0,
          }),
        );
        onImported?.(response);
        resetStateAndClose();
      },
      (err) => {
        setIsImporting(false);
        toast.error(err.message || "Unable to import Word content");
      },
    );
  }, [uploadedFile, ideaId, onImported, resetStateAndClose, t]);

  const canImport = useMemo(
    () => !!uploadedFile?.savedName && !isImporting,
    [uploadedFile, isImporting],
  );

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={open}
      onClose={resetStateAndClose}
      size="small"
    >
      <Modal.Header>{t("ideas.importWord.title")}</Modal.Header>
      <Modal.Content>
        <ol style={{ paddingLeft: 18, margin: 0 }}>
          <li style={{ marginBottom: 18 }}>
            <strong>{t("ideas.importWord.stepOne.title")}</strong>
            <p style={{ marginTop: 8 }}>{t("ideas.importWord.stepOne.info")}</p>
            <Button
              icon="download"
              content={t("ideas.download.word")}
              onClick={onDownloadWord}
            />
          </li>
          <li style={{ marginBottom: 18 }}>
            <strong>{t("ideas.importWord.stepTwo.title")}</strong>
            <p style={{ marginTop: 8 }}>{t("ideas.importWord.stepTwo.info")}</p>
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>{t("ideas.importWord.stepThree.title")}</strong>
            <p style={{ marginTop: 8 }}>
              {t("ideas.importWord.stepThree.info")}
            </p>
            <FileChooser
              forType="idea"
              forId={ideaId}
              maxMBFileSize={100}
              accept={DOCX_ACCEPT_TYPES}
              trigger={
                <Button icon="upload" content={"Select Word document"} />
              }
              onComplete={(savedName, fileName) => {
                setUploadedFile({ savedName, fileName });
              }}
            />
          </li>
        </ol>
        {uploadedFile ?
          <Message positive icon style={{ marginTop: 12 }}>
            <Icon name="file word" />
            <Message.Content>
              <Message.Header>{t("ideas.importWord.fileReady")}</Message.Header>
              {uploadedFile.fileName}
            </Message.Content>
          </Message>
        : null}

        <Message style={{ color: "grey" }} size="small">
          <span>
            We use an automated process to import your responses into the{" "}
            {t("generic.idea")}. We advise to check the results manually once
            the import is complete.
          </span>
        </Message>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={resetStateAndClose}>{t("generic.cancel")}</Button>
        <Button
          primary
          loading={isImporting}
          disabled={!canImport}
          onClick={importFromWord}
        >
          {t("ideas.importWord.confirm")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ImportWordModal;
